import {
  ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppRouterState } from 'app/_state/router/router-state';
import { MatDialog } from '@angular/material/dialog';
import { filter, finalize, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import {
  AddOrUpdateAddressDialog
} from 'entity/dialogs/add-or-update-address/add-or-update-address.dialog';
import { addressWasChangedOnNormalization, getSafe, sortByBest } from 'helpers';
import {
  UpdateGeocodeDialogComponent
} from 'shared/components/update-geocode/update-geocode.dialog';

import {
  AddressDetails, EntityWithAddresses, FieldStatus, FieldStatusInput, GetEntityById2GQL,
  RestoreOriginalAddressGQL,
  UpdateAddressGQL, UpdateAddressInput
} from 'generated/graphql';
import { selectCurrentCampaign } from 'app/_state/campaign-context/campaign-context-selectors';
import { ConfirmDialogComponent } from 'shared/components/confirm-dialog/confirm-dialog-component';
import { AddressPipe } from 'shared/pipes/address.pipe';

@Component({
  selector: 'app-entity-addresses-table',
  templateUrl: './entity-addresses-table.component.html'
})
export class EntityAddressesTableComponent
  extends WithDisposableSubject(WithChangesSubject(class { }))
  implements OnChanges, OnInit {
  isSaving = false;

  @Input() entityId: string;
  @Input() campaignId: string;
  @Input() showTaskDoneMark: boolean;
  @Input() readonly: boolean;

  entity: EntityWithAddresses.Fragment = null;
  showElectoralDistricts = false;

  showBad = false;

  sortByBest = sortByBest;

  constructor(private _store: Store<AppRouterState>,
    private _getEntityGQL: GetEntityById2GQL,
    private _updateAddressGQL: UpdateAddressGQL,
    private _restoreOriginalAddressGQL: RestoreOriginalAddressGQL,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _toastrService: ToastrService
  ) {

    super();

    this._store.select(
      selectCurrentCampaign
    ).pipe(
      takeUntil(this.disposer)
    ).subscribe(campaign => {
      this.showElectoralDistricts = campaign?.showElectoralDistricts;
    });
  }

  ngOnInit() {
    this.watchForChangeKeys('entityId', 'campaignId').pipe(
      startWith<any, any>(null),
      takeUntil(this.disposer),
      map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
      filter(({ campaignId, entityId }) => !!campaignId || !!entityId),
      switchMap(({ campaignId, entityId }) => this._getEntityGQL.watch({
        entityId,
        campaignId,
        withToplines: true,
        withAddresses: true,
        withBestAddress: true
      }, {
        fetchPolicy: 'cache-first'
      }).valueChanges.pipe(takeUntil(this.disposer))),
      map(({ data }) => data?.getEntity)
    ).subscribe((entity) => {
      this.entity = entity as any;
      this._cd.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  onAddNew() {
    AddOrUpdateAddressDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        address: null
      }
    });
  }

  onEdit(address: AddressDetails.Fragment) {
    AddOrUpdateAddressDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        address
      }
    });
  }

  onEditGeocode(address: AddressDetails.Fragment) {
    UpdateGeocodeDialogComponent.open(this._dialog, {
      data: { address }
    });
  }

  onRevertAutoCorrect(address: AddressDetails.Fragment) {
    const addressPipe = new AddressPipe();
    const dRef = ConfirmDialogComponent.open(this._dialog, {
      data: {
        title: 'Undo Address Autocorrect?',
        message: `
        The original address provided by the user was: ${addressPipe.transform(address.original)}.
        <br><br>
        Are you sure you would like to revert the address to the original?
        `,
      }
    });

    dRef
      .afterClosed()
      .pipe(
        filter(
          (result) => result === true
        ),
        switchMap(() => this._restoreOriginalAddressGQL.mutate({
          input: {
            addressId: address.id,
            entityId: this.entity.id,
          }
        }))
      ).subscribe(() => {
        this._toastrService.success('Address reverted');
      });
  }

  onSetStatus(address: AddressDetails.Fragment, status: FieldStatusInput) {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    if ([FieldStatusInput.Bad, FieldStatusInput.DoNotContact].includes(status)) {
      const shouldMarkBad = confirm(
        `Are you sure you would like to mark this address as ${status.split('_').join(' ')}?`
      );
      if (!shouldMarkBad) {
        this.isSaving = false;
        return;
      }
    }
    const input: UpdateAddressInput = {
      addressId: address.id,
      campaignId: this.campaignId,
      entityId: this.entity.id,
      changes: {
        status: status as FieldStatusInput
      }
    };

    this._updateAddressGQL.mutate({ input }).pipe(
      finalize(() => this.isSaving = false),
    ).subscribe(
      (_) => this._toastrService.success('Changes saved'),
    );
  }

  isNormalized(address: AddressDetails.Fragment) {
    return addressWasChangedOnNormalization(address);
  }

  isVerified(item: AddressDetails.Fragment) {
    return item.status === FieldStatus.Verified;
  }

  isBad(item: AddressDetails.Fragment) {
    return getSafe(() => item.status === 'bad', true);
  }

  isGeocodeBad(item: AddressDetails.Fragment) {
    return getSafe(() => item.geocodeBad, false);
  }

  isDoNotContact(item: AddressDetails.Fragment) {
    return item?.status === FieldStatus.DoNotContact;
  }

  getBadCount(items: AddressDetails.Fragment[]) {
    return getSafe(() => items.filter(i => this.isBad(i)).length, 0);
  }

  getDistricts(item: AddressDetails.Fragment) {
    return item.electoralDistricts || [];
  }
}
